// src/auth/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { secondsUntilExp } from "./jwt.ts";

type AuthCtx = {
  accessToken: string | null;
  setAccessToken: (t: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string>; // returns new access
};

const AuthContext = createContext<AuthCtx | null>(null);
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("wrap with <AuthProvider />");
  return ctx;
};

const API = "/api"; // adjust if not using /api prefix

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Single-flight refresh gate so multiple 401s don’t trigger many refreshes
  const refreshPromiseRef = useRef<Promise<string> | null>(null);
  const refresh = async (): Promise<string> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    refreshPromiseRef.current = (async () => {
      const res = await fetch(`${API}/auth/refresh`, {
        method: "POST",
        credentials: "include",          // sends the HttpOnly refresh cookie
      });
      if (!res.ok) {
        refreshPromiseRef.current = null;
        setAccessToken(null);
        throw new Error("refresh failed");
      }
      const data = await res.json();     // { access_token: "..." }
      setAccessToken(data.access_token);
      refreshPromiseRef.current = null;
      return data.access_token;
    })();
    return refreshPromiseRef.current;
  };

  // Proactive refresh ~30s before expiry (optional but smooth UX)
  useEffect(() => {
    if (!accessToken) return;
    const secs = secondsUntilExp(accessToken);
    if (!secs || secs <= 30) {
      refresh().catch(() => setAccessToken(null));
      return;
    }
    const id = setTimeout(() => {
      refresh().catch(() => setAccessToken(null));
    }, Math.max(0, (secs - 30) * 1000));
    return () => clearTimeout(id);
  }, [accessToken]);

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // sets the HttpOnly refresh cookie
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error("bad credentials");
    const data = await res.json();       // { access_token }
    setAccessToken(data.access_token);
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, {
      method: "POST",
      credentials: "include", // so server can clear cookie + revoke session
    }).catch(() => {});
    setAccessToken(null);
  };

  return (
    <AuthContext.Provider value={{ accessToken, setAccessToken, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};
